import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PublicHeader, Breadcrumbs, SeoHead } from "@/components/PublicSeo";
import { SeoFooter } from "@/components/SeoFooter";
import { PublicCreatorCard } from "@/components/PublicCreatorCard";
import NotFound from "@/pages/NotFound";
import { X } from "lucide-react";

const DIMS = [["category", "Category"], ["city", "City"], ["language", "Language"], ["platform", "Platform"]];

function Filters({ data, options, sort }) {
  const navigate = useNavigate();
  const current = Object.fromEntries(DIMS.map(([d]) => [d, data.dims[d]?.name || ""]));
  const go = async (next, nextSort = sort) => {
    const { data: r } = await api.get("/seo/path", { params: next });
    navigate(r.path + (nextSort !== "followers" ? `?sort=${nextSort}` : ""));
  };
  return (
    <div className="rounded-2xl border border-border bg-surface p-4 mb-6" data-testid="seo-filters">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {DIMS.map(([d, label]) => (
          <label key={d} className="text-xs text-muted-foreground">{label}
            <select value={current[d]} onChange={(e) => go({ ...current, [d]: e.target.value })} data-testid={`seo-filter-${d}`}
              className="mt-1 w-full h-10 rounded-lg border border-border bg-background px-2 text-sm text-foreground">
              <option value="">Any {label.toLowerCase()}</option>
              {d === "city"
                ? Object.entries((options?.city || []).reduce((acc, o) => { (acc[o.region || "Other"] ||= []).push(o); return acc; }, {})).map(([region, list]) => (
                  <optgroup key={region} label={region}>{list.map((o) => <option key={o.slug} value={o.name}>{o.name}</option>)}</optgroup>
                ))
                : (options?.[d] || []).map((o) => <option key={o.slug} value={o.name}>{o.name}</option>)}
            </select>
          </label>
        ))}
        <label className="text-xs text-muted-foreground">Sort by
          <select value={sort} onChange={(e) => go(current, e.target.value)} data-testid="seo-sort"
            className="mt-1 w-full h-10 rounded-lg border border-border bg-background px-2 text-sm text-foreground">
            <option value="followers">Most followers</option>
            <option value="verified">Verified first</option>
            <option value="newest">Newest</option>
          </select>
        </label>
      </div>
      <div className="flex flex-wrap gap-2 mt-3" data-testid="seo-filter-chips">
        {DIMS.filter(([d]) => current[d]).map(([d]) => (
          <button key={d} onClick={() => go({ ...current, [d]: "" })} data-testid={`seo-chip-${d}`}
            className="inline-flex items-center gap-1 rounded-full bg-brand-pink/10 text-brand-pink border border-brand-pink/30 px-3 py-1 text-xs font-semibold">
            {current[d]} <X className="h-3 w-3" />
          </button>
        ))}
        <Link to="/influencer-directory" data-testid="seo-reset-filters" className="text-xs font-semibold text-muted-foreground underline px-2 py-1">Reset filters</Link>
      </div>
    </div>
  );
}

function Pagination({ data, path }) {
  if (data.pages <= 1) return null;
  return (
    <nav aria-label="Pagination" className="flex flex-wrap gap-2 mt-8" data-testid="seo-pagination">
      {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
        <Link key={p} to={p === 1 ? path : `${path}?page=${p}`} aria-current={p === data.page ? "page" : undefined}
          className={`h-9 min-w-9 px-3 inline-flex items-center justify-center rounded-full border text-sm ${p === data.page ? "bg-foreground text-background border-foreground" : "border-border"}`}>{p}</Link>
      ))}
    </nav>
  );
}

export default function SeoLanding() {
  const location = useLocation();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [options, setOptions] = useState(null);
  const page = Number(params.get("page") || 1);
  const sort = params.get("sort") || "followers";

  useEffect(() => {
    setData(null);
    api.get("/seo/resolve", { params: { path: location.pathname, page, sort } }).then((r) => setData(r.data)).catch(() => setData({ status: "not_found" }));
    window.scrollTo(0, 0);
  }, [location.pathname, page, sort]);
  useEffect(() => { api.get("/seo/options").then((r) => setOptions(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="min-h-screen bg-background"><PublicHeader /><p className="p-10 text-muted-foreground">Loading…</p></div>;
  if (data.status === "not_found") return <NotFound />;
  if (data.status === "redirect") return <Navigate to={data.canonical_path + location.search} replace />;

  const jsonLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: data.breadcrumbs.map((b, i) => ({ "@type": "ListItem", position: i + 1, name: b.label, item: data.canonical_url.split("/").slice(0, 3).join("/") + b.path })),
  };
  return (
    <div className="min-h-screen bg-background" data-testid="seo-landing-page">
      <SeoHead title={data.title} description={data.description} canonical={data.canonical_url} robots={data.robots} jsonLd={jsonLd} />
      <PublicHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <Breadcrumbs items={data.breadcrumbs} />
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mb-4" data-testid="seo-h1">{data.h1}</h1>
        <p className="text-muted-foreground max-w-3xl mb-6" data-testid="seo-intro">{data.intro}</p>
        <Filters data={data} options={options} sort={sort} />
        <p className="text-sm text-muted-foreground mb-4" data-testid="seo-result-count">{data.total} creator{data.total === 1 ? "" : "s"}{data.sort_note ? ` · ${data.sort_note}` : ""}</p>
        {data.total === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="seo-empty">
            <p className="font-semibold mb-2">No public creators match this combination yet.</p>
            <p className="text-sm text-muted-foreground">Try removing a filter, or register as a creator to be listed here.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="seo-creator-grid">
            {data.creators.map((c) => <PublicCreatorCard key={c.slug} c={c} />)}
          </div>
        )}
        <Pagination data={data} path={data.canonical_path} />

        {data.location_cities?.length > 0 && (
          <section className="mt-12" data-testid="seo-location-cities">
            <h2 className="font-heading font-bold text-lg mb-3">Cities in {data.dims.city.name}</h2>
            <ul className="flex flex-wrap gap-2">
              {data.location_cities.map((r) => <li key={r.path}><Link to={r.path} className="inline-block rounded-full border border-border px-3 py-1.5 text-sm hover:border-brand-pink">{r.label} <span className="text-muted-foreground">({r.count})</span></Link></li>)}
            </ul>
          </section>
        )}

        {data.total > 0 && (
          <section className="mt-14 max-w-3xl" data-testid="seo-about">
            <h2 className="font-heading font-bold text-lg mb-3">About these creators</h2>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
              <li>{data.insights.verified} of {data.total} profiles show follower counts fetched from a platform, with the source and date on each card.</li>
              {data.insights.cities.length > 0 && <li>Cities represented: {data.insights.cities.join(", ")}.</li>}
              {data.insights.top_languages.length > 0 && <li>Most common content languages: {data.insights.top_languages.join(", ")}.</li>}
            </ul>
          </section>
        )}

        <section className="mt-12 max-w-3xl" data-testid="seo-faq">
          <h2 className="font-heading font-bold text-lg mb-3">Frequently asked questions</h2>
          {data.faqs.map((f) => (
            <details key={f.q} className="border-b border-border py-3">
              <summary className="font-semibold cursor-pointer">{f.q}</summary>
              <p className="text-sm text-muted-foreground mt-2">{f.a}</p>
            </details>
          ))}
        </section>

        {data.related.length > 0 && (
          <section className="mt-12" data-testid="seo-related">
            <h2 className="font-heading font-bold text-lg mb-3">Related pages</h2>
            <ul className="flex flex-wrap gap-2">
              {data.related.map((r) => <li key={r.path}><Link to={r.path} className="inline-block rounded-full border border-border px-3 py-1.5 text-sm hover:border-brand-pink">{r.label}</Link></li>)}
            </ul>
          </section>
        )}

        <section className="mt-14 rounded-3xl bg-brand-gradient text-white p-8 sm:p-12 grid md:grid-cols-3 gap-6 items-center" data-testid="seo-cta">
          <h2 className="font-heading font-black text-2xl md:col-span-1">Work with {data.h1.toLowerCase()}</h2>
          <p className="opacity-90 text-sm md:col-span-1">Post a paid or gift-hamper campaign, collect bids, and pay safely through escrow.</p>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to="/?join=brand" data-testid="seo-cta-brand" className="rounded-full bg-white text-foreground px-5 py-2.5 font-semibold text-sm">Register as a brand</Link>
            <Link to="/?join=creator" data-testid="seo-cta-creator" className="rounded-full border border-white/70 px-5 py-2.5 font-semibold text-sm">Join as a creator</Link>
          </div>
        </section>
      </main>
      <SeoFooter />
    </div>
  );
}
